import type { ReactNode } from 'react';
import type { NavigationBody } from '@ayumia/contracts';
import { formatRoute, type Route } from '../app/router';
import { Icon } from '../design-system/icons';
import { useLoad } from '../views/useLoad';
import { useAppServices } from '../views/ViewContext';
import { totalOf } from './useBadges';
import { labelOf, leaves, routeOfItem, type NavItem } from './navigation-model';

/**
 * Accueil simple (ADR-092 §5) : servi quand la navigation ne désigne aucun tableau de bord, ou que
 * celui-ci ne se résout pas pour cette identité. Un salut, le compteur « Mon travail » et les
 * raccourcis du menu (déjà élagué au serveur), groupés par module. Rien d'écrit en dur côté métier.
 */
export function HomePage({ navigation, name, onNavigate }: { navigation: NavigationBody; name: string | undefined; onNavigate: (route: Route) => void }) {
  const services = useAppServices();
  const [inbox] = useLoad<number | null>('home:inbox', async () => totalOf(await services.client.query('ListWorkInbox')));
  const shortcuts = leaves([...navigation.items, ...(navigation.footer_items ?? [])]).filter(({ item }) => !(item.target?.type === 'workspace' && item.target.ref === 'core.workspace.home'));
  const groups = new Map<string, { module: NavItem; items: NavItem[] }>();
  for (const { module, item } of shortcuts) {
    const group = groups.get(module.item_id) ?? { module, items: [] };
    group.items.push(item);
    groups.set(module.item_id, group);
  }

  const link = (route: Route, content: ReactNode, className: string) => (
    <a
      href={formatRoute(route)}
      className={className}
      onClick={(event) => {
        event.preventDefault();
        onNavigate(route);
      }}
    >
      {content}
    </a>
  );

  return (
    <section className="ay-home" aria-labelledby="ay-home-title">
      <h1 id="ay-home-title">{name === undefined ? 'Bonjour' : `Bonjour ${name}`}</h1>
      {inbox.status === 'ready' && inbox.value !== null && (
        <div className="ay-home__work">
          {link(
            { kind: 'inbox' },
            <>
              <Icon name="inbox" />
              <span className="ay-home__count">{inbox.value}</span>
              <span>{inbox.value <= 1 ? 'tâche dans Mon travail' : 'tâches dans Mon travail'}</span>
            </>,
            'ay-home__work-link',
          )}
        </div>
      )}
      {[...groups.values()].map(({ module, items }) => (
        <section key={module.item_id} className="ay-home__group" aria-label={labelOf(module)}>
          {items.length > 1 || items[0] !== module ? <h2>{labelOf(module)}</h2> : null}
          <ul className="ay-home__shortcuts">
            {items.map((item) => {
              const route = routeOfItem(item)!;
              return (
                <li key={item.item_id}>
                  {link(
                    route,
                    <>
                      <Icon name={item.icon ?? module.icon} />
                      <span>{labelOf(item)}</span>
                    </>,
                    'ay-home__shortcut',
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </section>
  );
}
